import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { select, Store } from '@ngrx/store';
import { PermissionService, ToastService, User } from '@workspace-app/shared';
import { selectUser } from '@workspace-app/state/global';
import { DocumentService } from '../../../../shared/services/document.service';
import { PdfViewerService } from '../../../../shared/services/pdf-viewer.service';
import {
  Bill,
  BillDataService,
  BillEntryPreview,
  BillRecipientsInfo,
  formatStaleReferencesWarning,
} from '../bill-data.service';
import { BILL_TYPE_LABELS } from '../billing.columns';
import { CancelBillDialogComponent } from '../dialogs/cancel-bill-dialog.component';
import { SendEmailDialogComponent } from '../dialogs/send-email-dialog.component';
import { BillingChecklistComponent } from '../components/billing-checklist.component';

@Component({
  selector: 'app-bill-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    TagModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    BillingChecklistComponent,
  ],
  providers: [ConfirmationService, DialogService],
  template: `
    <div class="flex flex-column gap-4 p-3" style="position: relative;">
      <!-- Loading Overlay for PDF Generation -->
      @if (generatingPdf()) {
        <div class="pdf-loading-overlay">
          <div class="flex flex-column align-items-center">
            <p-progressspinner />
            <p class="mt-3 text-600">Generating PDF preview...</p>
          </div>
        </div>
      }
      <!-- Header with status and actions -->
      <div class="flex justify-content-between align-items-center flex-wrap gap-2">
        <div class="flex align-items-center gap-3">
          <p-tag [value]="bill.status" [severity]="getStatusSeverity(bill.status)" />
          <span class="text-color-secondary">{{ billTypeLabel }}</span>
        </div>
        <div class="flex gap-2 flex-wrap">
          @if (bill.status === 'Draft' && canViewPdf()) {
            <p-button label="Preview" icon="pi pi-eye" severity="secondary" [outlined]="true" (onClick)="viewPdf()" />
          }
          @if (bill.status !== 'Draft' && canViewPdf()) {
            <p-button
              label="Download"
              icon="pi pi-download"
              severity="secondary"
              [outlined]="true"
              (onClick)="downloadPdf()" />
          }
          @if (canIssue() && hasRecipientEmail()) {
            <p-button label="Send Invoice" icon="pi pi-send" (onClick)="issueAndSendEmail()" />
          }
          @if (canIssue() && !hasRecipientEmail()) {
            <p-button label="Issue Invoice" icon="pi pi-check" severity="secondary" (onClick)="issueAndDownload()" />
          }
          @if (canSendEmail()) {
            <p-button
              label="Send Email"
              icon="pi pi-envelope"
              severity="info"
              [outlined]="true"
              (onClick)="openSendEmailDialog()" />
          }
          @if (canDiscard()) {
            <p-button
              label="Discard"
              icon="pi pi-trash"
              severity="danger"
              [outlined]="true"
              (onClick)="discardDraft()" />
          }
          @if (canMarkPaid()) {
            <p-button label="Mark as Paid" icon="pi pi-money-bill" severity="success" (onClick)="markAsPaid()" />
          }
          @if (canCancel()) {
            <p-button label="Cancel" icon="pi pi-times" severity="danger" [outlined]="true" (onClick)="cancelBill()" />
          }
          @if (canCreateNewVersion()) {
            <p-button
              label="New Version"
              icon="pi pi-copy"
              severity="secondary"
              [outlined]="true"
              (onClick)="createNewVersion()" />
          }
        </div>
      </div>

      <!-- Bill Info -->
      <div class="grid">
        <div class="col-12 md:col-6 lg:col-3">
          <div class="surface-ground p-3 border-round h-full">
            <div class="text-sm text-color-secondary mb-1">Recipient</div>
            <div class="font-semibold">{{ bill.recipientOrganisation?.name || 'N/A' }}</div>
          </div>
        </div>
        <div class="col-12 md:col-6 lg:col-3">
          <div class="surface-ground p-3 border-round h-full">
            <div class="text-sm text-color-secondary mb-1">Billing Year</div>
            <div class="font-semibold">{{ bill.billingYear }}</div>
          </div>
        </div>
        <div class="col-12 md:col-6 lg:col-3">
          <div class="surface-ground p-3 border-round h-full">
            <div class="text-sm text-color-secondary mb-1">Created</div>
            <div class="font-semibold">{{ bill.createdAt | date: 'mediumDate' }}</div>
          </div>
        </div>
        <div class="col-12 md:col-6 lg:col-3">
          <div class="surface-ground p-3 border-round h-full">
            <div class="text-sm text-color-secondary mb-1">PO Number</div>
            @if (bill.status === 'Draft') {
              <div class="flex align-items-center gap-2">
                <input
                  pInputText
                  type="text"
                  class="p-inputtext-sm w-full"
                  [ngModel]="poNumber()"
                  (ngModelChange)="poNumber.set($event)"
                  placeholder="Enter PO number"
                  (blur)="savePurchaseOrderNumber()" />
              </div>
            } @else {
              <div class="font-semibold">{{ bill.purchaseOrderNumber || '-' }}</div>
            }
          </div>
        </div>
        <div class="col-12 md:col-6 lg:col-3">
          <div class="surface-ground p-3 border-round h-full">
            <div class="text-sm text-color-secondary mb-1">
              @if (bill.status === 'Paid') {
                Paid
              } @else if (bill.status === 'Issued') {
                Issued
              } @else if (bill.status === 'Cancelled') {
                Cancelled
              } @else {
                Status
              }
            </div>
            <div class="font-semibold">
              @if (bill.status === 'Paid' && bill.paidAt) {
                {{ bill.paidAt | date: 'mediumDate' }}
              } @else if (bill.status === 'Issued' && bill.issuedAt) {
                {{ bill.issuedAt | date: 'mediumDate' }}
              } @else if (bill.status === 'Cancelled' && bill.cancelledAt) {
                {{ bill.cancelledAt | date: 'mediumDate' }}
              } @else {
                Draft
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Recipients -->
      @if (recipientsInfo(); as info) {
        <div class="surface-ground p-3 border-round">
          <div class="text-sm text-color-secondary mb-2">Invoice Recipient</div>
          @if (info.addressEmail) {
            <div class="text-sm mb-1">
              <span class="font-semibold">{{ info.addressEmail }}</span>
              <span class="text-color-secondary ml-1">({{ info.addressType }} address)</span>
            </div>
          } @else {
            <div class="text-sm text-orange-500 mb-1">No email configured on billing/business address</div>
          }
          @if (info.contacts.length > 0) {
            <div class="text-sm text-color-secondary mt-2 mb-1">Additional contacts</div>
            @for (c of info.contacts; track c.email) {
              <div class="text-sm">
                <span class="font-semibold">{{ c.name }}</span>
                @if (c.email) {
                  <span class="text-color-secondary ml-2">{{ c.email }}</span>
                }
              </div>
            }
          }
          @if (bill.emailSentAt) {
            <div class="text-sm text-green-600 mt-2">
              <i class="pi pi-check-circle mr-1"></i>Email sent on {{ bill.emailSentAt | date: 'medium' }}
            </div>
          }
        </div>
      }

      @if (bill.status === 'Cancelled' && bill.cancellationReason) {
        <div class="surface-ground p-3 border-round border-1 border-red-200">
          <div class="text-sm text-color-secondary mb-1">Cancellation Reason</div>
          <div>{{ bill.cancellationReason }}</div>
        </div>
      }

      <!-- Bill Entries Table -->
      <div class="flex flex-column gap-2">
        <h3 class="m-0">Bill Items</h3>
        @if (entries().length > 0) {
          <p-table [value]="entries()" [tableStyle]="{ 'min-width': '100%' }" styleClass="p-datatable-sm">
            <ng-template #header>
              <tr>
                <th>Description</th>
                <th>Category</th>
                <th>Item Type</th>
                <th class="text-right">Unit Amount</th>
                <th class="text-center">Qty</th>
                <th class="text-right">Total</th>
              </tr>
            </ng-template>
            <ng-template #body let-entry>
              <tr [class.pro-forma-entry]="entry.isProForma">
                <td>
                  {{ entry.description }}
                  @if (entry.isProForma) {
                    <span class="pro-forma-tag">Pro-forma</span>
                  }
                </td>
                <td>{{ entry.category }}</td>
                <td>{{ entry.itemType }}</td>
                <td class="text-right">{{ entry.unitAmount | currency: 'EUR' : 'symbol' : '1.2-2' }}</td>
                <td class="text-center">{{ entry.quantity }}</td>
                <td class="text-right">{{ entry.totalAmount | currency: 'EUR' : 'symbol' : '1.2-2' }}</td>
              </tr>
            </ng-template>
            <ng-template #footer>
              <tr>
                <td colspan="5" class="text-right font-bold">Subtotal</td>
                <td class="text-right font-bold">{{ bill.subtotal | currency: 'EUR' : 'symbol' : '1.2-2' }}</td>
              </tr>
              @if (bill.vatRate > 0) {
                <tr>
                  <td colspan="5" class="text-right">VAT ({{ bill.vatRate * 100 | number: '1.1-1' }}%)</td>
                  <td class="text-right">{{ bill.vatAmount | currency: 'EUR' : 'symbol' : '1.2-2' }}</td>
                </tr>
              }
              <tr class="font-bold">
                <td colspan="5" class="text-right text-lg">Total</td>
                <td class="text-right text-lg">{{ bill.totalAmount | currency: 'EUR' : 'symbol' : '1.2-2' }}</td>
              </tr>
            </ng-template>
          </p-table>
        } @else {
          <div class="p-3 surface-ground border-round text-center text-color-secondary">No bill items found</div>
        }
      </div>

      <!-- Billing Checklist -->
      <app-billing-checklist [billId]="bill.id" [readonly]="bill.status !== 'Draft'" />
    </div>
    <p-confirmdialog />
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
      .pdf-loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: var(--z-app-sticky);
      }
      :host ::ng-deep .pro-forma-entry td {
        color: var(--text-color-secondary) !important;
        font-style: italic;
      }
      .pro-forma-tag {
        display: inline-block;
        font-size: 0.7rem;
        font-style: normal;
        padding: 1px 6px;
        border-radius: 4px;
        background: var(--surface-200);
        color: var(--text-color-secondary);
        margin-left: 0.5rem;
        vertical-align: middle;
      }
    `,
  ],
})
export class BillViewComponent implements OnChanges {
  @Input({ required: true }) bill!: Bill;
  @Output() billUpdated = new EventEmitter<Bill>();

  private store = inject(Store);
  private billDataService = inject(BillDataService);
  private documentService = inject(DocumentService);
  private pdfViewerService = inject(PdfViewerService);
  private toastService = inject(ToastService);
  private permissionService = inject(PermissionService);
  private confirmationService = inject(ConfirmationService);
  private dialogService = inject(DialogService);
  private destroyRef = inject(DestroyRef);

  entries = signal<BillEntryPreview[]>([]);
  recipientsInfo = signal<BillRecipientsInfo | null>(null);
  currentUser = signal<User | undefined>(undefined);
  generatingPdf = signal(false);
  poNumber = signal<string>('');

  private cancelDialogRef?: DynamicDialogRef | null = null;

  get billTypeLabel(): string {
    return BILL_TYPE_LABELS[this.bill?.billType] ?? this.bill?.billType ?? '';
  }

  constructor() {
    this.store.pipe(select(selectUser), takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      this.currentUser.set(user);
    });
  }

  ngOnChanges(): void {
    // Sync PO number from bill
    this.poNumber.set(this.bill?.purchaseOrderNumber || '');

    // Parse entries from JSON when bill changes
    if (this.bill?.billEntriesJson) {
      try {
        const parsedEntries = JSON.parse(this.bill.billEntriesJson);
        // Normalize entries to handle both PascalCase and camelCase property names
        // (older entries might have PascalCase from before JsonPropertyName attributes were added)
        const normalizedEntries = parsedEntries.map((entry: Record<string, unknown>) => ({
          category: entry['category'] ?? entry['Category'],
          itemType: entry['itemType'] ?? entry['ItemType'],
          description: entry['description'] ?? entry['Description'],
          managementTier: entry['managementTier'] ?? entry['ManagementTier'],
          unitAmount: entry['unitAmount'] ?? entry['UnitAmount'],
          quantity: entry['quantity'] ?? entry['Quantity'],
          totalAmount: entry['totalAmount'] ?? entry['TotalAmount'],
          isProForma: entry['isProForma'] ?? entry['IsProForma'] ?? false,
        }));
        this.entries.set(normalizedEntries);
      } catch {
        console.error('Failed to parse bill entries JSON');
        this.entries.set([]);
      }
    } else {
      this.entries.set([]);
    }

    // Load recipients info
    if (this.bill?.id) {
      this.billDataService.getBillRecipients(this.bill.id).subscribe({
        next: (info) => this.recipientsInfo.set(info),
        error: () => this.recipientsInfo.set(null),
      });
    }
  }

  savePurchaseOrderNumber(): void {
    if (this.bill?.status !== 'Draft') return;
    const newValue = this.poNumber().trim() || null;
    if (newValue === (this.bill.purchaseOrderNumber || null)) return;

    this.billDataService.updateDraftBill(this.bill.id, { purchaseOrderNumber: newValue ?? '' }).subscribe({
      next: (updatedBill) => {
        this.billUpdated.emit(updatedBill);
        this.toastService.displaySuccessMessage('PO number updated');
      },
      error: (err) => {
        this.toastService.displayErrorMessage(err.error?.message || 'Failed to update PO number');
        this.poNumber.set(this.bill.purchaseOrderNumber || '');
      },
    });
  }

  getStatusSeverity(status: string): 'secondary' | 'info' | 'success' | 'danger' {
    switch (status) {
      case 'Draft':
        return 'secondary';
      case 'Issued':
        return 'info';
      case 'Paid':
        return 'success';
      case 'Cancelled':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  hasRecipientEmail(): boolean {
    return !!this.recipientsInfo()?.addressEmail;
  }

  // Permission checks
  private hasBillingAdmin(): boolean {
    const user = this.currentUser();
    return !!user && this.permissionService.hasAnyEffectivePermission(user, ['Billing.Admin']);
  }

  canViewPdf(): boolean {
    // Can view PDF for any bill that has entries (draft generates on-the-fly, issued uses stored)
    return !!this.bill && this.entries().length > 0;
  }

  canIssue(): boolean {
    return this.hasBillingAdmin() && this.bill?.status === 'Draft';
  }

  canDiscard(): boolean {
    return this.hasBillingAdmin() && this.bill?.status === 'Draft';
  }

  canMarkPaid(): boolean {
    return this.hasBillingAdmin() && this.bill?.status === 'Issued';
  }

  canCancel(): boolean {
    return this.hasBillingAdmin() && this.bill?.status === 'Issued';
  }

  canSendEmail(): boolean {
    return this.hasBillingAdmin() && this.bill?.status === 'Issued' && this.hasRecipientEmail();
  }

  canCreateNewVersion(): boolean {
    const status = this.bill?.status;
    // Cannot create new version from paid bills
    return this.hasBillingAdmin() && (status === 'Issued' || status === 'Cancelled');
  }

  // Actions
  downloadPdf(): void {
    if (!this.bill) return;

    if (this.bill.pdfDocumentId) {
      this.billDataService.getWorkflowDocument(this.bill.pdfDocumentId).subscribe({
        next: (doc) => {
          if (doc?.objectStoreRef) {
            this.documentService.downloadDocument(
              doc.objectStoreRef,
              doc.fileName || `${this.bill.completeBillNumber}.pdf`
            );
          } else {
            this.toastService.displayErrorMessage('PDF document not found');
          }
        },
        error: () => {
          this.toastService.displayErrorMessage('Failed to download PDF');
        },
      });
    } else {
      // Fallback: generate on-the-fly
      this.generatingPdf.set(true);
      this.billDataService.downloadPdf(this.bill.id).subscribe({
        next: (blob) => {
          this.generatingPdf.set(false);
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${this.bill.completeBillNumber}.pdf`;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.generatingPdf.set(false);
          this.toastService.displayErrorMessage('Failed to download PDF');
        },
      });
    }
  }

  viewPdf(): void {
    if (!this.bill) return;

    // For issued/paid bills with stored PDF, use the stored document
    if (this.bill.pdfDocumentId && this.bill.status !== 'Draft') {
      this.billDataService.getWorkflowDocument(this.bill.pdfDocumentId).subscribe({
        next: (doc) => {
          if (doc?.objectStoreRef) {
            this.documentService.viewDocument(
              doc.objectStoreRef,
              doc.fileName || `${this.bill.completeBillNumber}.pdf`,
              this.bill.completeBillNumber,
              'application/pdf'
            );
          } else {
            this.toastService.displayErrorMessage('PDF document not found');
          }
        },
        error: () => {
          this.toastService.displayErrorMessage('Failed to load PDF document');
        },
      });
    } else {
      // For draft bills, generate PDF on-the-fly
      this.generatingPdf.set(true);
      this.billDataService.downloadPdf(this.bill.id).subscribe({
        next: (blob) => {
          this.generatingPdf.set(false);
          const blobUrl = URL.createObjectURL(blob);
          this.pdfViewerService.openDocument({
            blobUrl,
            filename: `${this.bill.completeBillNumber}.pdf`,
            title: `${this.bill.completeBillNumber} (Preview)`,
          });
        },
        error: (err) => {
          this.generatingPdf.set(false);
          this.toastService.displayErrorMessage(err.error?.message || 'Failed to generate PDF preview');
        },
      });
    }
  }

  issueAndSendEmail(): void {
    this.billDataService.getStaleReferences(this.bill.id).subscribe({
      next: ({ items }) => this.confirmIssueAndSendEmail(formatStaleReferencesWarning(items)),
      error: () => this.confirmIssueAndSendEmail(null),
    });
  }

  private confirmIssueAndSendEmail(warning: string | null): void {
    const baseMessage = `This will issue invoice ${this.bill.completeBillNumber} and open the email composer. Continue?`;
    this.confirmationService.confirm({
      header: warning ? 'Send Invoice — stale references' : 'Send Invoice',
      message: warning ? `${warning} ${baseMessage}` : baseMessage,
      acceptLabel: warning ? 'Issue anyway' : 'Yes',
      accept: () => {
        this.billDataService.issueBill(this.bill.id).subscribe({
          next: (updatedBill) => {
            this.billUpdated.emit(updatedBill);
            this.toastService.displaySuccessMessage('Invoice issued');
            // Open email dialog with the issued bill
            this.openSendEmailDialog(updatedBill);
          },
          error: (err) => {
            this.toastService.displayErrorMessage(err.error?.message || 'Failed to issue invoice');
          },
        });
      },
    });
  }

  openSendEmailDialog(bill?: Bill): void {
    const targetBill = bill ?? this.bill;
    const emailDialogRef = this.dialogService.open(SendEmailDialogComponent, {
      header: `Send Email - ${targetBill.completeBillNumber}`,
      position: 'right',
      height: '100%',
      width: '450px',
      closable: true,
      draggable: false,
      modal: true,
      data: { bill: targetBill },
    });

    emailDialogRef?.onClose.subscribe((result: { sent: boolean; bill: Bill } | undefined) => {
      if (result?.sent && result.bill) {
        this.billUpdated.emit(result.bill);
      }
    });
  }

  issueAndDownload(): void {
    this.billDataService.getStaleReferences(this.bill.id).subscribe({
      next: ({ items }) => this.confirmIssueAndDownload(formatStaleReferencesWarning(items)),
      error: () => this.confirmIssueAndDownload(null),
    });
  }

  private confirmIssueAndDownload(warning: string | null): void {
    const baseMessage = `Are you sure you want to issue invoice ${this.bill.completeBillNumber}? The PDF will be downloaded.`;
    this.confirmationService.confirm({
      header: warning ? 'Issue Invoice — stale references' : 'Issue Invoice',
      message: warning ? `${warning} ${baseMessage}` : baseMessage,
      acceptLabel: warning ? 'Issue anyway' : 'Yes',
      accept: () => {
        this.generatingPdf.set(true);
        this.billDataService.issueBill(this.bill.id).subscribe({
          next: (updatedBill) => {
            this.billUpdated.emit(updatedBill);
            this.toastService.displaySuccessMessage('Invoice issued');
            // Download the PDF
            this.billDataService.downloadPdf(updatedBill.id).subscribe({
              next: (blob) => {
                this.generatingPdf.set(false);
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${updatedBill.completeBillNumber}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
              },
              error: () => {
                this.generatingPdf.set(false);
                this.toastService.displayErrorMessage('Invoice issued but PDF download failed');
              },
            });
          },
          error: (err) => {
            this.generatingPdf.set(false);
            this.toastService.displayErrorMessage(err.error?.message || 'Failed to issue invoice');
          },
        });
      },
    });
  }

  markAsPaid(): void {
    this.confirmationService.confirm({
      header: 'Mark as Paid',
      message: `Are you sure you want to mark bill ${this.bill.completeBillNumber} as paid?`,
      accept: () => {
        this.billDataService.markAsPaid(this.bill.id).subscribe({
          next: (updatedBill) => {
            this.billUpdated.emit(updatedBill);
            this.toastService.displaySuccessMessage('Bill marked as paid');
          },
          error: (err) => {
            this.toastService.displayErrorMessage(err.error?.message || 'Failed to mark bill as paid');
          },
        });
      },
    });
  }

  cancelBill(): void {
    this.cancelDialogRef = this.dialogService.open(CancelBillDialogComponent, {
      header: `Cancel Bill ${this.bill.completeBillNumber}`,
      width: '400px',
      modal: true,
      data: { bill: this.bill },
    });

    this.cancelDialogRef?.onClose.subscribe((result: { reason: string } | undefined) => {
      if (result?.reason) {
        this.billDataService.cancelBill(this.bill.id, result.reason).subscribe({
          next: (updatedBill) => {
            this.billUpdated.emit(updatedBill);
            this.toastService.displaySuccessMessage('Bill cancelled');
          },
          error: (err) => {
            this.toastService.displayErrorMessage(err.error?.message || 'Failed to cancel bill');
          },
        });
      }
      this.cancelDialogRef = null;
    });
  }

  createNewVersion(): void {
    this.confirmationService.confirm({
      header: 'Create New Version',
      message: `Are you sure you want to create a new version of bill ${this.bill.completeBillNumber}?`,
      accept: () => {
        this.billDataService.createNewVersion(this.bill.id).subscribe({
          next: (newBill) => {
            this.billUpdated.emit(newBill);
            this.toastService.displaySuccessMessage(`New version ${newBill.completeBillNumber} created`);
          },
          error: (err) => {
            this.toastService.displayErrorMessage(err.error?.message || 'Failed to create new version');
          },
        });
      },
    });
  }

  discardDraft(): void {
    this.confirmationService.confirm({
      header: 'Discard Draft',
      message: `Are you sure you want to discard draft bill ${this.bill.completeBillNumber}? This cannot be undone.`,
      accept: () => {
        this.billDataService.discardDraft(this.bill.id).subscribe({
          next: () => {
            this.toastService.displaySuccessMessage('Draft discarded');
            this.billUpdated.emit(undefined as unknown as Bill); // Signal to close the overlay
          },
          error: (err) => {
            this.toastService.displayErrorMessage(err.error?.message || 'Failed to discard draft');
          },
        });
      },
    });
  }
}
